import { Suspense, useState } from 'react';

import { IconHamburgerMenu } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { Navigate } from 'react-router';

import { BreadCrumbs } from './components/BreadCrumbs';
import { FilterDrawer, Filters } from './components/Filters';
import { ReferBanner } from './components/ReferBanner';
import { Sellers } from './components/Sellers';
import {
  useGetBuyerSellerDirectory,
  useGetApplication,
} from '../../../api/lending/lapiHooks';
import { CustomErrorBoundary } from '../../../AppWrapper';
import { useAccountNavigation } from '../../../hooks/useAccountNavigation';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import {
  SpendingLimitCard,
  SpendingLimitCardSkeleton,
} from '../components/SpendingLimitCard/SpendingLimitCard';
import { SpendingLimitDrawer } from '../components/SpendingLimitDrawer/SpendingLimitDrawer';
import { useRefreshSpendingLimits } from '../hooks/useRefreshSpendingLimits';

export function SellerDirectory() {
  const { getAccountUrl } = useAccountNavigation();
  const { sellers, loadingSellerDirectory } = useGetBuyerSellerDirectory();
  const { application: rawApplication, fetchApplication } = useGetApplication();
  const isMobile = useMediaQuery('(max-width: 624px)');

  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [isSpendingLimitDrawerOpen, setIsSpendingLimitDrawerOpen] =
    useState(false);

  const {
    refreshSpendingLimit,
    isLoading: loadingRefreshSpendingLimit,
    errorMessage: refreshSpendingLimitErrorMessage,
  } = useRefreshSpendingLimits(() => {
    fetchApplication();
  });

  const handleRefreshSpendingLimit = async () => {
    try {
      await refreshSpendingLimit();
    } catch {
      // do nothing
    }
  };

  if (!loadingSellerDirectory && !sellers) {
    return <Navigate to={getAccountUrl(window.location.search)} />;
  }

  return (
    <div className="min-h-[calc(100vh-72px)] bg-gradient-to-r pt-[112px]">
      <div
        className="mx-auto grid max-w-[1136px] gap-x-[40px] px-[20px] pb-[20px]"
        style={{
          gridTemplateAreas: isMobile
            ? "'crumbs' 'spendingLimit' 'sellers'"
            : "'crumbs crumbs' 'spendingLimit spendingLimit' 'filters sellers' 'banner sellers'",
          gridTemplateColumns: isMobile ? '1fr' : '300px 1fr',
          gridTemplateRows: isMobile ? 'auto auto 1fr' : 'auto auto auto 1fr',
        }}
      >
        <div
          className="mb-2xl flex flex-row items-center gap-m"
          style={{ gridArea: 'crumbs' }}
        >
          <BreadCrumbs />
        </div>
        <div className="mb-m" style={{ gridArea: 'spendingLimit' }}>
          <CustomErrorBoundary errorComponent={<></>}>
            <Suspense fallback={<SpendingLimitCardSkeleton />}>
              <SpendingLimitCard
                setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
                refreshSpendingLimit={handleRefreshSpendingLimit}
                loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
              />
            </Suspense>
          </CustomErrorBoundary>
          <SpendingLimitDrawer
            isOpen={isSpendingLimitDrawerOpen}
            setIsOpen={setIsSpendingLimitDrawerOpen}
            refreshSpendingLimit={handleRefreshSpendingLimit}
            loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
            errorMessage={refreshSpendingLimitErrorMessage}
            application={rawApplication}
          />
        </div>
        {!isMobile && (
          <div style={{ gridArea: 'filters' }}>
            <Filters
              sellers={sellers}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
            />
          </div>
        )}
        {!isMobile && (
          <div className="mt-2xl" style={{ gridArea: 'banner' }}>
            <ReferBanner />
          </div>
        )}
        <div style={{ gridArea: 'sellers' }} data-testid="sellers">
          {isMobile && (
            <Button
              variant="tertiary"
              onClick={() => setFilterDrawer(true)}
              className="mb-m inline-flex p-0"
            >
              <IconHamburgerMenu />
              Filter by suppliers type
            </Button>
          )}
          <Sellers sellers={sellers} filter={selectedFilter} />
        </div>
      </div>
      <FilterDrawer
        isOpen={filterDrawer}
        onClose={() => setFilterDrawer(false)}
        sellers={sellers}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
    </div>
  );
}
